/**
 * PATH: packages/kanban-host/src/KanbanDocumentOverlay.tsx
 * FILE_ID: KANBANHOST-DOC-OVERLAY-001
 * PURPOSE: The host-injected in-card document editor (ARC-2). Fetches an
 *          attachment's bytes through the auth-gated email endpoint, then
 *          mounts the CANONICAL platform editor inside a full-screen
 *          portal: @quotech/email-hub PdfEditor for PDFs, MarkupDrawer
 *          for images. PDF saves land as NEW QDrive versions.
 * WHY:     Operator 2026-07-02 (U11): "PDFs and images and such should
 *          always open inside … reusing … PDF editors … we have already
 *          built up." @quotech/ui cannot import email-hub (cycle), so
 *          this component is injected via
 *          `KanbanEmailAdapter.DocumentEditorOverlay` — the same seam
 *          precedent as `downloadAttachment`.
 * FLOW:
 *   ┌────────────┐   ┌───────────────────┐   ┌──────────────────────────┐
 *   │ attachment │ → │ emailApiFetch     │ → │ PdfEditor (pdf) or       │
 *   │ (chip tap) │   │ download_url →    │   │ MarkupDrawer (image)     │
 *   │            │   │ bytes / objectURL │   │ save → qdrive replace    │
 *   └────────────┘   └───────────────────┘   └──────────────────────────┘
 * DEPENDENCIES:
 *   - @quotech/email-hub PdfEditor / MarkupDrawer: canonical editors —
 *     ALWAYS lazy (pdfjs-dist + pdf-lib ≈ 600 KB + worker).
 *   - ./qdriveVersions saveFileVersion: versioned save (replace+snapshot).
 * RELATED:
 *   - packages/quote-builder/src/components/drawers/FileMarkupOverlay.tsx: the mount
 *     precedent this mirrors (presigned bytes + onSaveBytes flow).
 *   - packages/ui/src/kanban/components/card-detail/CardFilesRail.tsx:
 *     the consumer (renders via the adapter seam).
 */

import { Suspense, lazy, useCallback, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { Loader2 } from 'lucide-react'

import { emailApiFetch } from '@quotech/email-hub/feature/lib/api'
import type { KanbanDocumentOverlayProps } from '@quotech/ui/kanban'
import {
  isOversizedAttachment,
  oversizedAttachmentMessage,
} from '@quotech/ui/kanban'

import { getKanbanApi } from './api'
import { saveFileVersion } from './qdriveVersions'
import { scopedAttachmentUrl } from './scopedAttachmentUrl'

// Lazy so the heavy pdf.js + pdf-lib bundle only loads when an operator
// actually opens a document — keeps the kanban entry chunk lean.
const PdfEditor = lazy(
  () => import('@quotech/email-hub/feature/components/PdfEditor'),
)
const MarkupDrawer = lazy(() =>
  import('@quotech/email-hub/feature/components/MarkupDrawer').then((m) => ({
    default: m.MarkupDrawer,
  })),
)

type DocKind = 'pdf' | 'image'

function kindOf(att: KanbanDocumentOverlayProps['attachment']): DocKind {
  const mime = (att.mime_type ?? att.content_type ?? '').toLowerCase()
  if (att.is_pdf || mime.includes('pdf')) return 'pdf'
  return 'image'
}

const overlayShell = 'fixed inset-0 z-[70] flex items-stretch bg-black/70'
const centered = 'flex h-full w-full items-center justify-center'

export function KanbanDocumentOverlay({
  attachment,
  emailId,
  cardId,
  onClose,
  onSaved,
  onFollowUpCreated,
}: KanbanDocumentOverlayProps) {
  const kind = kindOf(attachment)
  const [bytes, setBytes] = useState<ArrayBuffer | null>(null)
  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Fetch the raw bytes through the auth-gated endpoint (a bare <img>/
  // iframe URL would 401 — same reason downloadAttachment exists). PDFs
  // need an ArrayBuffer for pdf.js/pdf-lib; images get an object URL for
  // the markup canvas.
  useEffect(() => {
    let cancelled = false
    let objectUrl: string | null = null
    setBytes(null)
    setImageUrl(null)
    setError(null)
    ;(async () => {
      try {
        if (!attachment.download_url) throw new Error('no-url')
        // Two byte sources share this overlay (ARC-2 MR7):
        //  - email attachments: RELATIVE api path → auth-gated emailApiFetch
        //    with scope=all (BUG-0018 sibling: without it the visibility
        //    gate 404s teammate-synced attachments on this shared surface).
        //  - manual card uploads: ABSOLUTE presigned S3 URL → plain fetch
        //    (a bearer header alongside query-string auth is rejected by
        //    S3 — same rule as distributeFileVersion's version fetch).
        const url = attachment.download_url
        const res = /^https?:\/\//i.test(url)
          ? await fetch(url)
          : await emailApiFetch(scopedAttachmentUrl(url, cardId))
        if (!res.ok) throw new Error(`status ${res.status}`)
        if (kind === 'pdf') {
          const buf = await res.arrayBuffer()
          if (!cancelled) setBytes(buf)
        } else {
          const blob = await res.blob()
          objectUrl = URL.createObjectURL(blob)
          if (!cancelled) setImageUrl(objectUrl)
        }
      } catch {
        if (!cancelled) {
          setError('Could not load this file. Try downloading it instead.')
        }
      }
    })()
    return () => {
      cancelled = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [attachment.id, attachment.download_url, kind, cardId])

  // Versioned save: annotated bytes REPLACE the live QDrive blob; the
  // backend snapshots the prior blob to a FileVersion first, so v1 stays
  // recoverable in the version rail. Only offered when the attachment's
  // bytes were qdrive-ingested (qdrive_file_id present) — otherwise the
  // editor's built-in "Save to qDrive" (anchorEmailId) still works.
  const qdriveFileId = attachment.qdrive_file_id
  const handleSaveBytes = useCallback(
    async (annotated: Uint8Array, name: string) => {
      if (!qdriveFileId) return
      await saveFileVersion(
        qdriveFileId,
        annotated,
        name || attachment.filename || 'document.pdf',
        'application/pdf',
      )
      onSaved?.()
      onClose()
    },
    [qdriveFileId, attachment.filename, onSaved, onClose],
  )

  // ARC-2/U12 (highlight → curated follow-up): the editor hands us the
  // extracted page PNGs + the operator's note. Preferred path (U12
  // closeout): mint ONE real follow-up SUB-CARD under this card via the
  // ARC-3 entries endpoint (provider='entry' child on the parent_card
  // tree — it shows up in the drawer's Entries table and the board like
  // any dependency sub-card), then hang the extracted pages off the
  // MINTED sub-card as attachments. Hosts/backends that predate
  // createCardEntries keep the legacy pages-on-parent + comment flow.
  const handleCreateFollowUp = useCallback(
    async (payload: {
      note: string
      pages: Array<{ pageNumber: number; filename: string; blob: Blob }>
    }) => {
      if (!cardId) return
      const api = getKanbanApi()
      if (!api.uploadCardAttachment) {
        throw new Error('Card attachments are not available on this board')
      }
      // BUG-2026-07-09-KANBAN-ATTACH-UPLOAD-NO-CLIENT-GUARDS — same BE
      // endpoint as the drawer's manual upload: size-gate every page PNG
      // against the ONE shared cap before anything uploads, naming each
      // oversize file, instead of burning a 413 mid-batch.
      const oversize = payload.pages.filter((p) => isOversizedAttachment(p.blob.size))
      if (oversize.length > 0) {
        throw new Error(
          oversize
            .map((p) => oversizedAttachmentMessage(p.filename, p.blob.size))
            .join(' '),
        )
      }
      const pageRefs = payload.pages.map((p) => `p${p.pageNumber}`).join(', ')
      const source = attachment.filename || 'document'
      const note = (payload.note || '').trim()

      if (api.createCardEntries) {
        // Entry title renders as "action — what" (card_entries.py). Keep
        // the action imperative and short; the source doc + pages ride
        // in `what` so the sub-card is self-describing on the board.
        const action =
          note.length > 0 && note.length <= 120 ? note : `Follow up: ${source}`
        const { created } = await api.createCardEntries(cardId, {
          rows: [{ action, what: `${source} — ${pageRefs}` }],
        })
        const subCard = created[0]
        if (subCard) {
          for (const p of payload.pages) {
            await api.uploadCardAttachment(
              subCard.id,
              new File([p.blob], p.filename, { type: 'image/png' }),
            )
          }
          // A note too long for the title survives verbatim as the
          // sub-card's first comment.
          if (note.length > 120) {
            await api.createComment(subCard.id, note)
          }
          onFollowUpCreated?.()
          return
        }
      }

      // Legacy fallback — pages + one comment on the PARENT card.
      for (const p of payload.pages) {
        await api.uploadCardAttachment(
          cardId,
          new File([p.blob], p.filename, { type: 'image/png' }),
        )
      }
      const body = [
        `Follow-up from "${source}" — highlighted ${pageRefs} attached.`,
        note,
      ]
        .filter(Boolean)
        .join('\n\n')
      await api.createComment(cardId, body)
      onFollowUpCreated?.()
    },
    [cardId, attachment.filename, onFollowUpCreated],
  )

  const loadingView = (
    <div className={`${centered} gap-2 text-white`}>
      <Loader2 className="h-6 w-6 animate-spin" aria-hidden /> Loading document…
    </div>
  )

  if (kind === 'image') {
    if (error) {
      return createPortal(<ErrorShell message={error} onClose={onClose} />, document.body)
    }
    // MarkupDrawer renders its own fixed full-screen chrome (it returns
    // null while src is null) — keep our loading shell up until the
    // object URL lands so the operator sees immediate feedback.
    return createPortal(
      imageUrl ? (
        <Suspense fallback={<div className={overlayShell}>{loadingView}</div>}>
          <MarkupDrawer
            src={imageUrl}
            anchorEmailId={emailId ?? null}
            onClose={onClose}
          />
        </Suspense>
      ) : (
        <div className={overlayShell}>{loadingView}</div>
      ),
      document.body,
    )
  }

  if (error) {
    return createPortal(<ErrorShell message={error} onClose={onClose} />, document.body)
  }

  return createPortal(
    <div className={overlayShell}>
      {!bytes ? (
        loadingView
      ) : (
        <Suspense fallback={loadingView}>
          <PdfEditor
            pdfBytes={bytes}
            filename={attachment.filename || 'document.pdf'}
            onClose={onClose}
            anchorEmailId={emailId ?? null}
            onSaveBytes={qdriveFileId ? handleSaveBytes : undefined}
            saveLabel="Save version"
            onCreateFollowUp={cardId ? handleCreateFollowUp : undefined}
          />
        </Suspense>
      )}
    </div>,
    document.body,
  )
}

function ErrorShell({ message, onClose }: { message: string; onClose: () => void }) {
  return (
    <div className={overlayShell}>
      <div className="m-auto flex max-w-sm flex-col items-center gap-3 rounded-xl bg-white p-6 text-center shadow-2xl dark:bg-slate-900">
        <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
          {message}
        </p>
        <button
          type="button"
          onClick={onClose}
          className="rounded-md bg-primary px-4 py-1.5 text-xs font-semibold text-primary-foreground hover:bg-primary/90"
        >
          Close
        </button>
      </div>
    </div>
  )
}

export default KanbanDocumentOverlay
